import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Card,
  CardActionArea,
  CardContent,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import VisaoRestritaBanner from '../VisaoRestritaBanner';
import AlertarAssinaturaPendenteModal from './AlertarAssinaturaPendenteModal';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../../utils/dataIsolation';

const TERMINAL_STATUSES = [
  { id: 'AUTORIZADO', label: 'Autorizado', color: '#2e7d32', bg: 'rgba(46,125,50,0.08)' },
  { id: 'NEGADO', label: 'Rejeitado', color: '#c62828', bg: 'rgba(198,40,40,0.08)' },
];

const STATUS_CHIP = {
  PENDENTE: { label: 'Pendente', color: '#ef6c00', bg: 'rgba(239,108,0,0.12)' },
  EM_ANDAMENTO: { label: 'Assinatura', color: '#c62828', bg: 'rgba(198,40,40,0.12)' },
  AUTORIZADO: { label: 'Autorizado', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  NEGADO: { label: 'Rejeitado', color: '#c62828', bg: 'rgba(198,40,40,0.12)' },
};

function classificarFluxoPagamento(fluxo) {
  const n = String(fluxo?.nome || '').toLowerCase();
  return n.includes('pagamento');
}

function mapearAtividadeParaEtapa(atv, etapas) {
  const status = String(atv.status || '').toUpperCase();
  if (status === 'AUTORIZADO') return { tipo: 'terminal', id: 'AUTORIZADO' };
  if (status === 'NEGADO') return { tipo: 'terminal', id: 'NEGADO' };
  const idx = Number.isFinite(Number(atv.etapaAtual)) ? Number(atv.etapaAtual) : 0;
  const etapa = etapas[Math.max(0, Math.min(idx, Math.max(etapas.length - 1, 0)))];
  return etapa ? { tipo: 'etapa', id: etapa.id, index: idx } : { tipo: 'etapa', id: null, index: 0 };
}

function aguardandoAssinatura(atv) {
  return String(atv.status || '').toUpperCase() === 'EM_ANDAMENTO';
}

function ProcessoCard({ atv, selected, onSelect, onAbrirAssinatura, onIrAtividades, compact }) {
  const st = STATUS_CHIP[atv.status] || STATUS_CHIP.PENDENTE;
  const assinando = aguardandoAssinatura(atv);
  return (
    <Card
      elevation={0}
      sx={{
        mb: 1,
        borderRadius: 2,
        border: selected ? '2px solid #1565c0' : assinando ? '1px solid rgba(198,40,40,0.45)' : '1px solid rgba(0,0,0,0.08)',
        bgcolor: selected ? 'rgba(21,101,192,0.06)' : assinando ? 'rgba(198,40,40,0.04)' : '#fff',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <CardActionArea onClick={() => onSelect(atv.id)}>
        <CardContent sx={{ p: compact ? 1.25 : 1.5, '&:last-child': { pb: compact ? 1.25 : 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ lineHeight: 1.25 }}>
              {atv.numeroProcesso}
            </Typography>
            <Chip
              size="small"
              label={st.label}
              sx={{ height: 22, fontSize: 10, fontWeight: 700, color: st.color, bgcolor: st.bg }}
            />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mt: 0.5,
            }}
          >
            {atv.empresa || '—'}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
            {atv.setor || 'Sem setor'}
          </Typography>
        </CardContent>
      </CardActionArea>
      {(assinando || selected) && (
        <Stack direction="row" gap={0.5} sx={{ px: 1, pb: 1 }} flexWrap="wrap">
          {(atv.avaliacaoAssinaturaDocumentoId || atv.assinaturaDocumentoId) && (
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DrawOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onAbrirAssinatura(atv);
              }}
              sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
            >
              Assinar
            </Button>
          )}
          {selected && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onIrAtividades();
              }}
              sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
            >
              Atividades
            </Button>
          )}
        </Stack>
      )}
    </Card>
  );
}

function KanbanColuna({ titulo, subtitulo, count, badgeAssinatura, color, bg, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 260,
        maxWidth: 280,
        flex: '0 0 260px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: `1px solid ${color}33`,
        bgcolor: bg || '#f8fafc',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: `3px solid ${color}`, bgcolor: '#fff' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ color, lineHeight: 1.3 }}>
            {titulo}
          </Typography>
          <Chip
            size="small"
            label={count}
            sx={{ height: 22, fontWeight: 800, bgcolor: color, color: '#fff', minWidth: 28 }}
          />
        </Stack>
        {subtitulo && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {subtitulo}
          </Typography>
        )}
        {badgeAssinatura > 0 && (
          <Chip
            size="small"
            icon={<DrawOutlinedIcon sx={{ fontSize: '12px !important' }} />}
            label={`${badgeAssinatura} aguardando assinatura`}
            sx={{ mt: 0.75, height: 22, fontSize: 10, bgcolor: 'rgba(198,40,40,0.1)', color: '#c62828' }}
          />
        )}
      </Box>
      <Box sx={{ p: 1, flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 320px)', minHeight: 200 }}>
        {children}
      </Box>
    </Paper>
  );
}

function FluxogramaResumo({ etapas, buckets, selecionadoId, onSelect }) {
  const totalAtivos = Object.values(buckets.porEtapa).reduce((s, arr) => s + arr.length, 0);
  return (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Stack direction="row" alignItems="stretch" gap={0} sx={{ minWidth: 'fit-content' }}>
        {etapas.map((etapa, index) => {
          const itens = buckets.porEtapa[etapa.id] || [];
          const assinando = itens.filter(aguardandoAssinatura).length;
          const pct = totalAtivos > 0 ? Math.round((itens.length / totalAtivos) * 100) : 0;
          const exigeAss = etapa.exigeAssinatura !== false && etapa.exige_assinatura !== 0;
          return (
            <React.Fragment key={etapa.id}>
              {index > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, color: '#90a4ae', fontSize: 18 }}>
                  ›
                </Box>
              )}
              <Paper
                elevation={0}
                sx={{
                  width: 140,
                  p: 1.25,
                  borderRadius: 2,
                  border: itens.length ? '1px solid #1565c0' : '1px solid #e0e0e0',
                  bgcolor: itens.length ? 'rgba(21,101,192,0.06)' : '#fafafa',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  {index + 1}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.25, minHeight: 36, fontSize: 12 }}>
                  {etapa.nome}
                </Typography>
                {exigeAss && (
                  <DrawOutlinedIcon sx={{ fontSize: 14, color: '#c62828', mt: 0.25 }} titleAccess="Exige assinatura" />
                )}
                <Typography variant="h6" fontWeight={800} color="primary" sx={{ my: 0.5 }}>
                  {itens.length}
                </Typography>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: '#e3f2fd',
                    overflow: 'hidden',
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: '#1565c0' }} />
                </Box>
                {assinando > 0 && (
                  <Typography variant="caption" color="error" fontWeight={700}>
                    {assinando} assinatura
                  </Typography>
                )}
                <Stack spacing={0.35} sx={{ mt: 0.75, maxHeight: 72, overflowY: 'auto' }}>
                  {itens.slice(0, 4).map((atv) => (
                    <Chip
                      key={atv.id}
                      size="small"
                      label={atv.numeroProcesso}
                      onClick={() => onSelect(atv.id)}
                      color={aguardandoAssinatura(atv) ? 'error' : 'default'}
                      variant={selecionadoId === atv.id ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: 10, maxWidth: '100%' }}
                    />
                  ))}
                </Stack>
              </Paper>
            </React.Fragment>
          );
        })}
        {TERMINAL_STATUSES.map((term) => {
          const itens = buckets.terminais[term.id] || [];
          return (
            <React.Fragment key={term.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, color: '#90a4ae', fontSize: 18 }}>›</Box>
              <Paper
                elevation={0}
                sx={{
                  width: 110,
                  p: 1.25,
                  borderRadius: 2,
                  border: `2px solid ${term.color}`,
                  bgcolor: term.bg,
                  textAlign: 'center',
                  alignSelf: 'center',
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: term.color }}>
                  {term.label}
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: term.color, mt: 0.5 }}>
                  {itens.length}
                </Typography>
              </Paper>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

export default function NotasFiscaisFluxoView() {
  const navigate = useNavigate();
  const { user, rbacRole, fiscalSetor, unidadeSelecionada } = useAuth();
  const scopeOpts = { user, rbacRole, fiscalSetor };

  const [loading, setLoading] = useState(true);
  const [fluxo, setFluxo] = useState(null);
  const [atividades, setAtividades] = useState([]);
  const [erro, setErro] = useState('');
  const [selecionadoId, setSelecionadoId] = useState(null);
  const [alertarOpen, setAlertarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('kanban');

  const load = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) params.set('setor', setorScope);

      const pathAtiv = withSetorScope(
        `/api/notas-fiscais/atividades-processo-pagamento${params.toString() ? `?${params.toString()}` : ''}`,
        scopeOpts
      );
      const [resFluxos, resAtiv] = await Promise.all([
        apiFetch('/api/contratos/fluxos'),
        apiFetch(pathAtiv),
      ]);
      const fluxos = resFluxos.ok ? await resFluxos.json() : [];
      const listaFluxos = Array.isArray(fluxos) ? fluxos : fluxos?.fluxos || [];
      const fluxoPag = listaFluxos.find(classificarFluxoPagamento) || null;
      setFluxo(fluxoPag);

      const data = resAtiv.ok ? await resAtiv.json() : [];
      const lista = (Array.isArray(data) ? data : []).map((row) => ({
        id: row.id,
        numeroProcesso: row.numero_processo,
        empresa: row.empresa,
        setor: row.setor,
        status: row.status,
        etapaAtual: row.etapaAtual ?? row.etapa_atual ?? 0,
        fluxoId: row.fluxoId ?? row.fluxo_id ?? null,
        assinaturaDocumentoId: row.assinaturaDocumentoId || null,
        avaliacaoAssinaturaDocumentoId: row.avaliacaoAssinaturaDocumentoId || null,
        criadoEm: row.criado_em,
      }));
      setAtividades(lista);
    } catch (e) {
      console.error(e);
      setErro(e.message || 'Erro ao carregar fluxo de pagamento.');
      setAtividades([]);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada?.codigo, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    load();
  }, [load]);

  const etapas = useMemo(() => {
    const list = Array.isArray(fluxo?.etapas) ? [...fluxo.etapas] : [];
    return list.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [fluxo]);

  const buckets = useMemo(() => {
    const porEtapa = {};
    etapas.forEach((e) => {
      porEtapa[e.id] = [];
    });
    const terminais = { AUTORIZADO: [], NEGADO: [] };
    const aguardando = [];

    atividades.forEach((atv) => {
      const map = mapearAtividadeParaEtapa(atv, etapas);
      if (map.tipo === 'terminal') {
        terminais[map.id]?.push(atv);
      } else if (map.id != null && porEtapa[map.id]) {
        porEtapa[map.id].push(atv);
      } else if (etapas[0]) {
        porEtapa[etapas[0].id].push(atv);
      }
      if (aguardandoAssinatura(atv)) aguardando.push(atv);
    });

    return { porEtapa, terminais, aguardando };
  }, [atividades, etapas]);

  const selecionado = useMemo(
    () => atividades.find((a) => a.id === selecionadoId) || null,
    [atividades, selecionadoId]
  );

  const abrirAssinatura = (atv) => {
    const id = atv.avaliacaoAssinaturaDocumentoId || atv.assinaturaDocumentoId;
    if (!id) return;
    navigate(`/assinaturas/documentos/${id}`, {
      state: { proximoAssinaturaDocumentoId: atv.assinaturaDocumentoId },
    });
  };

  const irAtividades = () => navigate('/notas-fiscais', { state: { menu: 'atividades-notas-fiscais' } });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <VisaoRestritaBanner />
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <AccountTreeIcon color="primary" />
            <Typography variant="h5" fontWeight={700} color="primary">
              Fluxo — Processo de Pagamento
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Acompanhe etapas, assinaturas pendentes e acione alertas em lote.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="kanban" sx={{ textTransform: 'none', px: 1.5 }}>
              <ViewKanbanIcon fontSize="small" sx={{ mr: 0.75 }} /> Kanban
            </ToggleButton>
            <ToggleButton value="fluxo" sx={{ textTransform: 'none', px: 1.5 }}>
              <AccountTreeIcon fontSize="small" sx={{ mr: 0.75 }} /> Fluxograma
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Envia alerta no sininho do destinatário + e-mail (seleção múltipla)">
            <span>
              <Button
                variant="contained"
                color="error"
                startIcon={<NotificationsActiveIcon />}
                onClick={() => setAlertarOpen(true)}
                disabled={buckets.aguardando.length === 0}
              >
                Alertar pendências
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Atualizar
          </Button>
        </Stack>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {!fluxo && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nenhum fluxo de Pagamento configurado. Cadastre em Configuração de Fluxos.
        </Alert>
      )}

      {/* Resumo de assinaturas */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          border: '1px solid rgba(198,40,40,0.25)',
          bgcolor: 'rgba(198,40,40,0.04)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DrawOutlinedIcon sx={{ color: '#c62828' }} />
            <Typography variant="subtitle2" fontWeight={700} color="#c62828">
              Aguardando assinatura digital
            </Typography>
            <Chip
              size="small"
              label={`${buckets.aguardando.length}`}
              sx={{ fontWeight: 800, bgcolor: 'rgba(198,40,40,0.15)', color: '#c62828' }}
            />
          </Stack>
          {buckets.aguardando.length > 0 && (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {buckets.aguardando.slice(0, 6).map((atv) => (
                <Chip
                  key={atv.id}
                  size="small"
                  color="error"
                  variant={selecionadoId === atv.id ? 'filled' : 'outlined'}
                  label={atv.numeroProcesso}
                  onClick={() => setSelecionadoId(atv.id)}
                  onDelete={
                    atv.assinaturaDocumentoId || atv.avaliacaoAssinaturaDocumentoId
                      ? () => abrirAssinatura(atv)
                      : undefined
                  }
                  deleteIcon={
                    <Tooltip title="Abrir assinatura">
                      <DrawOutlinedIcon />
                    </Tooltip>
                  }
                />
              ))}
              {buckets.aguardando.length > 6 && (
                <Chip size="small" label={`+${buckets.aguardando.length - 6}`} />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {viewMode === 'fluxo' && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
            Visão em fluxograma (quantidade por etapa)
          </Typography>
          <FluxogramaResumo
            etapas={etapas}
            buckets={buckets}
            selecionadoId={selecionadoId}
            onSelect={setSelecionadoId}
          />
        </Paper>
      )}

      {viewMode === 'kanban' && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 2,
            alignItems: 'flex-start',
          }}
        >
          {etapas.map((etapa, index) => {
            const itens = buckets.porEtapa[etapa.id] || [];
            const assinando = itens.filter(aguardandoAssinatura).length;
            const exigeAss = etapa.exigeAssinatura !== false && etapa.exige_assinatura !== 0;
            return (
              <KanbanColuna
                key={etapa.id}
                titulo={`${index + 1}. ${etapa.nome}`}
                subtitulo={exigeAss ? 'Etapa com assinatura digital' : etapa.grupo_responsavel || etapa.grupo || null}
                count={itens.length}
                badgeAssinatura={assinando}
                color="#1565c0"
                bg="#f5f9ff"
              >
                {itens.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                    Nenhum processo
                  </Typography>
                ) : (
                  itens.map((atv) => (
                    <ProcessoCard
                      key={atv.id}
                      atv={atv}
                      selected={selecionadoId === atv.id}
                      onSelect={setSelecionadoId}
                      onAbrirAssinatura={abrirAssinatura}
                      onIrAtividades={irAtividades}
                      compact
                    />
                  ))
                )}
              </KanbanColuna>
            );
          })}
          {TERMINAL_STATUSES.map((term) => {
            const itens = buckets.terminais[term.id] || [];
            return (
              <KanbanColuna
                key={term.id}
                titulo={term.label}
                count={itens.length}
                badgeAssinatura={0}
                color={term.color}
                bg={term.bg}
              >
                {itens.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                    —
                  </Typography>
                ) : (
                  itens.map((atv) => (
                    <ProcessoCard
                      key={atv.id}
                      atv={atv}
                      selected={selecionadoId === atv.id}
                      onSelect={setSelecionadoId}
                      onAbrirAssinatura={abrirAssinatura}
                      onIrAtividades={irAtividades}
                      compact
                    />
                  ))
                )}
              </KanbanColuna>
            );
          })}
        </Box>
      )}

      {selecionado && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid rgba(21,101,192,0.25)',
            bgcolor: 'rgba(21,101,192,0.04)',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {selecionado.numeroProcesso} — {selecionado.empresa}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status: {selecionado.status} · Etapa: {(Number(selecionado.etapaAtual) || 0) + 1} · Setor: {selecionado.setor || '—'}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 1.5 }} flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={irAtividades}>
              Ir para Atividades
            </Button>
            {(selecionado.avaliacaoAssinaturaDocumentoId || selecionado.assinaturaDocumentoId) && (
              <Button
                size="small"
                variant="contained"
                startIcon={<DrawOutlinedIcon />}
                onClick={() => abrirAssinatura(selecionado)}
              >
                Continuar assinatura
              </Button>
            )}
            {aguardandoAssinatura(selecionado) && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<NotificationsActiveIcon />}
                onClick={() => setAlertarOpen(true)}
              >
                Alertar signatário
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <AlertarAssinaturaPendenteModal
        open={alertarOpen}
        onClose={() => setAlertarOpen(false)}
        user={user}
        rbacRole={rbacRole}
        fiscalSetor={fiscalSetor}
        unidadeSelecionada={unidadeSelecionada}
        onSuccess={(data) => {
          setSnackbar({
            open: true,
            message: data?.message || 'Alertas enviados com sucesso.',
            severity: data?.falhas ? 'warning' : 'success',
          });
          window.dispatchEvent(
            new CustomEvent('contratos:refresh', {
              detail: { source: 'fluxo-nf', tipo: 'alerta_assinatura' },
            })
          );
          load();
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
